import { useMedplum } from "@medplum/react";

import { getActiveOrganizationId } from "@/utils/medplum";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { EncounterProceduresScope } from "../constants/procedures";
import {
  EncounterChildSaveCoordinator,
  EncounterChildSaveOptions,
  isEncounterSignAndLock,
  useRegisterEncounterFinalize,
} from "../contexts/EncounterChildSaveContext";
import { captureEncounterChargeItems } from "../services/encounter-charge-items";
import { getEncounterProceduresAndConditions } from "../services/procedures";
import { reportPlaceOfServiceRefreshFailure } from "../utils/report-place-of-service-refresh-failure";

type UseEncounterChargeCaptureParams = {
  coordinator: EncounterChildSaveCoordinator;
  clientId: string | undefined;
  encounterId: string | undefined;
};

// Only the encounter's id reaches Sentry, and the toast says nothing about the client or what was
// documented — a failure to bill is an operational problem, not a clinical one.
const reportFailure = (encounterId: string, error: unknown) => {
  captureExceptionWithoutPhi("Failed to capture the encounter's charge items", error, {
    encounterId,
  });
  toast({
    title: "Failed to create the billing charges",
    message:
      "The encounter was signed and locked, but its procedures did not reach billing. Contact support if the error persists.",
    type: "error",
    autoClose: 5000,
  });
};

// The charges are in and only the encounter's queue label is missing, so this goes to Sentry without
// a toast: the encounter still shows up on Missing Services, and there is nothing the provider could
// do about which of its two labels it shows up under.
const reportServicesStatusTagFailure = (encounterId: string, servicesStatusTagFailure: string) => {
  captureExceptionWithoutPhi(
    "Captured the encounter's charges but failed to tag it as having unsent services",
    new Error(servicesStatusTagFailure),
    { encounterId },
  );
};

/**
 * Captures the billing copy of an encounter's procedures when the encounter is signed and locked.
 *
 * Registered as a finalizer, so it runs once every section has written and reads the procedures the
 * encounter ended up holding rather than racing the writes that produce them. It belongs to the
 * encounter rather than to the procedures tab: the charges have to be captured whether or not the
 * provider ever opened that tab.
 *
 * Never on a plain Save — a saved encounter is still being edited, and a procedure removed before
 * signing must not leave a charge behind.
 */
function useEncounterChargeCapture({
  coordinator,
  clientId,
  encounterId,
}: UseEncounterChargeCaptureParams): void {
  const medplum = useMedplum();
  // The practice whose fee definition the charges are priced against. A session without an active
  // practice still captures — the charges point at the global fee definition instead — because a
  // missing practice is a pricing question, not a reason to leave the encounter uncharged.
  const practiceOrganizationId = getActiveOrganizationId();

  // A failure is reported but never rethrown: the encounter's own signing has already gone through by
  // the time finalizers run and it goes on to be locked either way, so failing here would only strand
  // the provider. Nothing partial is left behind — the charges go out as one transaction — and the
  // record of what was charged lives on the charges themselves, so an amendment signs the encounter
  // again and captures whatever is missing.
  //
  // Not a useCallback: useRegisterEncounterFinalize keeps the latest render's handler.
  const captureOnSignAndLock = async (options: EncounterChildSaveOptions) => {
    if (!clientId || !encounterId || !isEncounterSignAndLock(options)) return;

    try {
      // Everything the encounter is billed for, whoever recorded it. Procedures already charged are
      // skipped by the capture itself.
      const { procedures } = await getEncounterProceduresAndConditions({
        medplum,
        clientId,
        encounterId,
        scope: EncounterProceduresScope.All,
      });

      const result = await captureEncounterChargeItems({
        medplum,
        clientId,
        encounterId,
        procedures,
        practiceOrganizationId,
      });

      // An encounter with nothing left to charge is the normal outcome of re-signing one, so only a
      // real failure is worth telling anyone about.
      if (result.ok || result.reason === "accounts_failed") {
        if (result.servicesStatusTagFailure) {
          reportServicesStatusTagFailure(encounterId, result.servicesStatusTagFailure);
        }
        if (result.placeOfServiceRefreshFailure) {
          reportPlaceOfServiceRefreshFailure(encounterId, result.placeOfServiceRefreshFailure);
        }
      }

      // A linkage failure can arrive with a failed tag or Location move as well. Each is reported:
      // the toast below covers the linkage, and the others went to Sentry above.
      if (result.ok) return;

      const reason = result.details ? `${result.reason}: ${result.details}` : result.reason;
      reportFailure(encounterId, new Error(reason));
    } catch (error) {
      reportFailure(encounterId, error);
    }
  };

  useRegisterEncounterFinalize(coordinator, captureOnSignAndLock);
}

export default useEncounterChargeCapture;
